import { Box, IconButton, InputAdornment, TextField, useTheme } from '@mui/material';
import { SearchBarProps } from 'src/features/sites/types/search';
import { mapSearchTextfield, mapSearchTextfieldBox } from '../../map-search/style';
import { useEffect, useState } from 'react';
import { useDebounce } from 'src/hooks/useDebounce';
import { SearchFillIcon } from 'src/assets/icons/SearchFillIcon';
import { CloseIconV2 } from 'src/assets/icons/CloseIconV2';

export const SiteSearchBar = ({
  setSearchValue,
  iconRight,
  iconRightInside = false,
  placeholder,
  setSites,
  onBlur,
  onFocus,
  width,
  height = '55px',
  debounceTime = 500,
}: SearchBarProps) => {
  const theme = useTheme();

  const disabledTextColor = theme.palette.text.disabled;

  const [innerState, setInnerState] = useState('');

  const handleClearSearch = () => {
    if (setSites) {
      setSites({ value: [], loaded: false });
    }

    setSearchValue('');
    setInnerState('');
  };

  const debouncedInnerSearch = useDebounce(innerState, debounceTime);

  useEffect(() => {
    setSearchValue(debouncedInnerSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInnerSearch]);

  const clearButton = innerState ? (
    <IconButton onClick={handleClearSearch}>
      <CloseIconV2 fill={theme.palette.error.main} />
    </IconButton>
  ) : null;

  // On the mobile/tablet map search the filter icon lives inside the input,
  // right-aligned; elsewhere it stays outside the field as before.
  const showIconInside = iconRightInside && !!iconRight;

  const endAdornment =
    clearButton || showIconInside ? (
      <>
        {clearButton}
        {showIconInside && iconRight}
      </>
    ) : null;

  return (
    <Box
      sx={{
        width: width ? width : '100%',
        ...(iconRight && mapSearchTextfieldBox),
      }}
    >
      <TextField
        data-testid="site-search"
        sx={{
          backgroundColor: theme.palette.background.default,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: theme.palette.divider,
          flex: '30%',
          '& fieldset': { border: 'none' },
          ...(!innerState ? mapSearchTextfield : {}),
        }}
        onChange={(e) => setInnerState(e.target.value)}
        value={innerState}
        name="search"
        fullWidth
        placeholder={placeholder ?? 'Search for Site Name or Address'}
        inputProps={{ style: { padding: '10px' }, 'data-testid': 'search-input' }}
        InputProps={{
          sx: {
            height,
          },
          startAdornment: (
            <InputAdornment position="start">
              <SearchFillIcon fill={disabledTextColor} />
            </InputAdornment>
          ),
          endAdornment: endAdornment,
          onBlur: onBlur,
          onFocus: onFocus,
        }}
      />
      {!iconRightInside && iconRight}
    </Box>
  );
};
